/**
 * Generate Blueprint Tool Schema (Orchestrator-level)
 *
 * Generates a visual blueprint layout diagram before calling writeSlide.
 */

import { z } from "zod";

export const BlueprintContextImageSchema = z.object({
    imageId: z.string().describe("Context image ID in cache (e.g., slide image, logo asset)"),
    purpose: z.string().describe("What this image is for (reference, logo, alignment, etc.)"),
});

export const GenerateBlueprintParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    slideIndex: z.number()
        .int()
        .min(1)
        .describe("1-based slide index to generate the blueprint for."),
    intent: z.string()
        .describe("Describe the layout change you want. Be specific about what is being added, removed, moved, or resized."),
    constraints: z.string()
        .optional()
        .describe("Explicitly list sections or elements that must NOT change. If you don't mention a section, Gemini may move it."),
    slideImageId: z.string()
        .optional()
        .describe("Image ID from a previous readSlide call (e.g. slide_318_img_12345). Reuses that image instead of re-capturing the slide."),
    contextImages: z.array(BlueprintContextImageSchema)
        .optional()
        .describe("Context images for the visual guide (logos/assets to add, reference slides for styling/alignment)."),
});

export type GenerateBlueprintParams = z.infer<typeof GenerateBlueprintParamsSchema>;

export interface GenerateBlueprintResult {
    success: boolean;
    blueprintId?: string;
    originalSlideImageId?: string;
    message?: string;
    error?: string;
    _imageIds?: string[];
}
